import { useCallback, useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import {
  MODEL_RUNS_TABLE_ASSETS,
  MODEL_RUNS_TABLE_ASSETS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import { MODEL_RUNS_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import useGroupByParam from 'shared/hooks/useGroupByParam';
import useGroupedTableData from 'shared/hooks/useGroupedTableData';
import { formatGroupTitle, getGroupValueFilter } from 'shared/utils/utils';
import {
  getModelRunsAssetsFilterOptionsQuery,
  getModelRunsTableAssetsQuery,
} from '../api';
import {
  COLUMN_TO_FIELD_MAP,
  FIELD_TO_COLUMN_MAP,
  GROUP_TITLE_FORMATTERS,
  GROUPABLE_COLUMN_IDS,
} from '../consts';
import {
  ModelRunsAssetsFilterOptionsResponse,
  ModelRunsTableAssetItem,
  ModelRunsTableAssetsPagedResponse,
} from '../types';
import { parseTableAssetToModelRuns } from '../utils';
import useModelRunsQueryContext from './useModelRunsQueryContext';

const mapModelRunsRows = (items: ModelRunsTableAssetItem[]) =>
  items.map(parseTableAssetToModelRuns);

type Args = {
  search?: string;
};

const useGroupedModelRuns = ({ search }: Args) => {
  const { groupBy, onGroupByChange } = useGroupByParam({
    paramKey: MODEL_RUNS_GROUP_BY,
    groupableColumnIds: GROUPABLE_COLUMN_IDS,
  });
  const isGrouped = !!groupBy;

  const {
    selectedEnvId,
    selectedTreeNodeIdsHash,
    hasEmptyTreeSelection,
    urlFiltersDebounced,
    urlSort,
    sort,
    filters,
    assetFilters,
    filterOptions,
  } = useModelRunsQueryContext({ search });

  const groupEnabled = isGrouped && !hasEmptyTreeSelection;
  const field = groupBy ? COLUMN_TO_FIELD_MAP[groupBy] : undefined;

  const groupableColumnIds = useMemo(
    () =>
      GROUPABLE_COLUMN_IDS.filter(
        (columnId) => (filterOptions[columnId]?.length ?? 0) > 0,
      ),
    [filterOptions],
  );

  const { data: groupOptionsData, isFetching: isGroupOptionsFetching } =
    useGraphqlQuery<ModelRunsAssetsFilterOptionsResponse>({
      query: getModelRunsAssetsFilterOptionsQuery,
      queryKey: [
        MODEL_RUNS_TABLE_ASSETS_FILTER_OPTIONS,
        selectedEnvId,
        selectedTreeNodeIdsHash,
        'grouped',
        urlFiltersDebounced,
        filters.search,
      ],
      variables: {
        filters,
        assetFilters: assetFilters.length ? assetFilters : null,
      },
      enabled: groupEnabled,
      envId: selectedEnvId,
      keepPreviousData: true,
    });

  const groupValues = useMemo(() => {
    if (!groupBy) return [];
    const parsed = parseGraphqlFilterOptions(
      groupOptionsData?.tableAssetsFilterOptions || [],
      FIELD_TO_COLUMN_MAP,
    );
    return parsed[groupBy] ?? [];
  }, [groupOptionsData, groupBy]);

  const nonEmptyGroupValues = useMemo(
    () => groupValues.map(({ value }) => value).filter(Boolean),
    [groupValues],
  );

  const formatTitle = useCallback(
    (value: string) =>
      groupBy
        ? formatGroupTitle(GROUP_TITLE_FORMATTERS, groupBy, value)
        : value,
    [groupBy],
  );

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<
      ModelRunsTableAssetItem,
      ModelRunsTableAssetsPagedResponse
    > => {
      const groupFilter = field
        ? getGroupValueFilter(field, value, nonEmptyGroupValues)
        : undefined;
      return {
        queryKey: [
          MODEL_RUNS_TABLE_ASSETS,
          selectedEnvId,
          selectedTreeNodeIdsHash,
          urlFiltersDebounced,
          urlSort,
          filters.search,
          groupFilter,
        ],
        query: getModelRunsTableAssetsQuery,
        variables: {
          filters,
          assetFilters: groupFilter ? [...assetFilters, groupFilter] : null,
          sort,
          limit: PAGE_SIZE,
          daysBack: DEFAULT_DAYS_BACK,
        },
        select: (response) => response.tableAssets,
        enabled: groupEnabled && !!groupFilter,
        envId: selectedEnvId,
      };
    },
    [
      field,
      nonEmptyGroupValues,
      selectedEnvId,
      selectedTreeNodeIdsHash,
      urlFiltersDebounced,
      urlSort,
      filters,
      assetFilters,
      sort,
      groupEnabled,
    ],
  );

  const {
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    isAnyFetchingWithRows,
  } = useGroupedTableData({
    groupBy,
    groupValues,
    enabled: !!field,
    formatGroupTitle: formatTitle,
    buildQueryOptions,
    mapItems: mapModelRunsRows,
  });

  const tableData = useMemo(
    () => groupedData.flatMap((group) => group.data),
    [groupedData],
  );

  return {
    groupBy,
    onGroupByChange,
    isGrouped,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    tableData,
    isFetching: isGroupOptionsFetching || isAnyFetchingWithRows,
  };
};

export default useGroupedModelRuns;
